import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import pdfLogo from '../../assets/images/pdfLogo.png';
import pdfGradient from '../../assets/images/pdfGradient.png';
import { getCaseValueText, onlyDateFormat } from '../../utils/helpers';
import CustomPieChart from '../../components/CustomPieChart';
import CurveChart from '../../components/CurveChart';
import { REPORT_SCOPES } from '../../reports/reportRequest';
import ReportAttachment from './ReportAttachment';
import { asArray, displayValue, displayDate, reportView } from './reportView';
import '../../index.css';
import './report.css';

export const useStyles = makeStyles(() => {
  return {
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 40px",
      background: "#fff",
      position: "fixed",
      top: "0",
      left: "0",
      width: "100%",
      height: "70px",
      boxShadow: "0px 2px 16px 0px rgba(0, 0, 0, 0.08)",
      "@media print": {
        display: "none",
      },
    },
    container: {
      padding: "50px",
      "@media print": {
        padding: "40px 0",
      },
    },
    pdfViewerContainer: {
      width: "100%" /* Full width of the parent */,
      // paddingTop: "141.42%" /* Height / Width * 100 for A4 aspect ratio */,
      // position: "relative",
    },
    subitem_gap: {
      padding: "0 16px",
      fontSize: "20px",
    },
    page_break: {
      "@media print": {
        padding: "0",
        pageBreakAfter: "always",
      },
    },
    padding_40: {
      padding: "40px 0",
      // "@media print": {
      //   padding: "40px 0",
      // },
    },
    bgColor: {
      "& > :nth-child(odd)": {
        backgroundColor: "#F4F6F8",
      },
      "@media print": {
        "& > :nth-child(odd)": {
          backgroundColor: "#F4F6F8",
        },
      },
    },
    pdfBgImage: {
      backgroundImage: `url(${pdfGradient}) `,
      backgroundRepeat: "no-repeat",
      backgroundSize: "cover",
      backgroundPosition: "right top",
      display: "flex",
      width: "100%",
      gap: "40px",
      padding: "40px",
      "@media print": {
        height: "100vh",
        padding: "40px 0",
        backgroundImage: `url(${pdfGradient})`,
        pageBreakAfter: "always",
      },
    },
  };
});

function ReportRecords({ title, rows, columns, classes, details = [] }) {
  if (!rows.length) return null;
  return <Stack gap="12px" className={classes.page_break}>
    <Typography variant="subtitle2" fontSize="22px">{title}</Typography>
    <TableContainer><Table><TableHead><TableRow>{columns.map(([key, name]) => <TableCell key={key}>{name}</TableCell>)}</TableRow></TableHead>
      <TableBody className={classes.bgColor}>{rows.map((row, index) => <TableRow key={`${row.source_table || ''}:${row.id || index}`}>
        {columns.map(([key, , render]) => <TableCell key={key}>{render ? render(row) : displayValue(row[key])}</TableCell>)}
      </TableRow>)}</TableBody>
    </Table></TableContainer>
    {rows.map((row, index) => {
      const fields = details.filter(([key]) => row[key] != null);
      return fields.length > 0 && <Stack key={row.id || index} px="16px" className={classes.bgColor}>
        <Typography variant="body1" fontWeight="600" p="6px 16px">{row.title || `Record ${row.id}`}</Typography>
        {fields.map(([key, name]) => <Box display="flex" p="6px 16px" key={key}>
          <Typography width="185px" flexShrink={0} variant="body1" fontWeight="600">{name}</Typography>
          <Typography variant="body1">{displayValue(row[key])}</Typography>
        </Box>)}
      </Stack>;
    })}
  </Stack>;
}

export default function ReportContent({ report, onReady }) {
  const classes = useStyles();
  const { clientAndCaseDetail, clientName, painRows, chartDates, allBodyParts, intakeRows, documents, expenses, appointments } = reportView(report);
  const pieChartStatsState = report.report_stats || {};
  const total = ['worst_pain_entries', 'severe_pain_entries', 'moderate_pain_entries', 'mild_pain_entries', 'no_pain_entries'].reduce((sum, key) => sum + Number(pieChartStatsState[key] || 0), 0);
  const percentage = key => (total ? Number(pieChartStatsState[key] || 0) / total * 100 : 0).toFixed(2);
  const worstPainPercentage = Number(percentage('worst_pain_entries'));
  const mildPainPercentage = Number(percentage('mild_pain_entries'));
  const severePainPercentage = Number(percentage('severe_pain_entries'));
  const noPainPercentage = Number(percentage('no_pain_entries'));
  const [logoReady, setLogoReady] = useState(false);
  const [attachmentStatus, setAttachmentStatus] = useState({});
  const handleAttachmentStatus = useCallback((key, status) => setAttachmentStatus(previous =>
    previous[key] === status ? previous : { ...previous, [key]: status }), []);
  const count = (report.manifest.sections?.documents === false ? 0 : documents.length) +
    (report.manifest.sections?.expenses === false ? 0 : expenses.reduce((sum, expense) => sum + asArray(expense.expense_attachments).length, 0));
  const ready = logoReady && Object.values(attachmentStatus).filter(status => status === 'ready').length === count;
  useEffect(() => { onReady?.(ready); }, [ready, onReady]);
  return (
      <div
      // style={{
      //   position: "absolute",
      //   top: 0,
      //   left: 0,
      //   height: "100%",
      //   width: "100%",
      // }}
      >
        <Stack
          bgcolor="#fff"
          paddingTop="74px"
          sx={{ "@media print": { paddingTop: "0" } }}
        >
          <Stack className={classes.pdfBgImage}>
            <Stack paddingLeft="40px">
              <Typography
                variant="body1"
                fontWeight="500"
                fontSize="20px"
                ml="16px"
              >
                Powered By
              </Typography>
              <Box width="347px">
                <img
                  src={pdfLogo}
                  onLoad={() => setLogoReady(true)} onError={() => setLogoReady(true)}
                  width="100%"
                  alt="mycasecare logo"
                />
              </Box>
            </Stack>
            <Typography
              color="#0D467F"
              variant="h1"
              fontSize="80px"
              width="460px"
              lineHeight="116%"
              pb="32px"
              paddingLeft="40px"
            >
              Case Summary Report
            </Typography>
            <Box
              borderRadius="24px 24px 24px 0px"
              bgcolor="#0D467F"
              padding="20px"
              width="fit-content"
              marginLeft="40px"
            >
              <Typography
                variant="h2"
                fontSize="32px"
                fontWeight="300"
                color="#fff"
              >
                {clientName}{" "}
                {clientAndCaseDetail?.case?.opposing_party &&
                  ` vs ${clientAndCaseDetail?.case?.opposing_party}`}
              </Typography>
            </Box>
            <Box
              borderRadius="0px 65px 0px 0px"
              maxWidth="660px"
              width="100%"
              bgcolor="#fff"
              padding="40px"
            >
              <Typography variant="body1" fontSize="16px" fontWeight="400">
                A concise overview of the individual's pain experiences,
                including trends, frequency, duration, and impact on daily
                activities. This report is essential for presenting a compelling
                argument in personal injury claims.
              </Typography>
              {report.manifest.scope !== 'all' && <Typography variant="body1" fontSize="14px" mt={2}>
                {REPORT_SCOPES[report.manifest.scope]}
              </Typography>}
              {(report.manifest.from_date || report.manifest.to_date) && <Typography variant="body1" fontSize="14px" mt={1}>
                {report.manifest.from_date ? displayDate(report.manifest.from_date) : 'No start limit'} — {report.manifest.to_date ? displayDate(report.manifest.to_date) : 'No end limit'}
              </Typography>}
            </Box>
            <Box
              display="flex"
              alignItems="flex-end"
              justifyContent="space-between"
              gap="8px"
              padding="0 40px"
            >
              <Stack gap="4px">
                {clientAndCaseDetail?.case?.agency?.name && (
                  <Typography variant="h5" fontSize="24px" color="#0D467F">
                    {clientAndCaseDetail?.case?.agency?.name}
                  </Typography>
                )}
                {clientAndCaseDetail?.case?.agency?.primary_contact_number && (
                  <Typography
                    variant="h6"
                    fontSize="18px"
                    color="#0D467F"
                    fontWeight="400"
                  >
                    {clientAndCaseDetail?.case?.agency?.primary_contact_number}
                  </Typography>
                )}
                {clientAndCaseDetail?.case?.agency?.primary_email && (
                  <Typography
                    variant="h6"
                    fontSize="18px"
                    color="#0D467F"
                    fontWeight="400"
                  >
                    {clientAndCaseDetail?.case?.agency?.primary_email}
                  </Typography>
                )}
                {clientAndCaseDetail?.case?.agency?.website && (
                  <Typography
                    variant="h6"
                    fontSize="18px"
                    color="#0D467F"
                    fontWeight="400"
                  >
                    {clientAndCaseDetail?.case?.agency?.website}
                  </Typography>
                )}
              </Stack>
              <Typography
                component="a"
                variant="h6"
                fontSize="18px"
                color="#3B3B3B"
              >
                www.mycasecare.com
              </Typography>
            </Box>
          </Stack>

          <Stack className={classes.container}>
            {/* TABLE OF CONTENTS */}
            <Stack gap="28px" padding="0 40px" className={classes.page_break}>
              <Typography variant="subtitle2" fontSize="50px" pb="18px">
                Table of Contents
              </Typography>
              <Typography variant="subtitle2" fontSize="22px">
                1. Introduction
              </Typography>
              <Stack gap="16px">
                <Typography variant="subtitle2" fontSize="22px">
                  2. Victim Information
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  2.1. Basic Information{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  2.2. Work Details{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  2.3. Hobbies & Lifestyle{" "}
                </Typography>
              </Stack>
              <Stack gap="16px">
                <Typography variant="subtitle2" fontSize="22px">
                  3. Case Details
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  3.1. Basic Information{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  3.2. Intake Information{" "}
                </Typography>
              </Stack>
              <Stack gap="16px">
                <Typography variant="subtitle2" fontSize="22px">
                  4. Injuries and Sufferings
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  4.1. Pain Report{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  4.1.1. Pain Trends{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  4.1.2. Pain Frequency{" "}
                </Typography>
                <Typography variant="subtitle2" className={classes.subitem_gap}>
                  4.1.3. Additional Statistics{" "}
                </Typography>
              </Stack>
              <Typography
                variant="subtitle2"
                fontSize="22px"
                // sx={{
                //   "@media print": {
                //     pageBreakBefore: "always",
                //   },
                // }}
              >
                5. Financial Impact
              </Typography>
              <Typography variant="subtitle2" fontSize="22px">
                6. Detailed Pain Logs
              </Typography>
              <Typography variant="subtitle2" fontSize="22px">
                7. Detailed Event Logs
              </Typography>
              <Typography variant="subtitle2" fontSize="22px">
                8. Supporting Documents
              </Typography>
            </Stack>

            <Stack
              gap="32px"
              component="section"
              className={classes.padding_40}
            >
              {/* BASIC INFORMATION */}
              <Stack gap="16px">
                <Typography variant="subtitle2" fontSize="22px">
                  1. Introduction
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontSize="16px"
                  fontWeight="400"
                >
                  {`This comprehensive report meticulously outlines the intricate
                details of ${clientName}'s personal
                injury incident on ${displayDate(
                  clientAndCaseDetail?.case?.incident_date
                )}. It is crafted to present a
                thorough understanding of the medical journey, pain management,
                appointments, and the profound impact on ${
                  clientName
                }'s daily
                life. The primary objective is to substantiate ${
                  clientName
                }'s
                case for compensation and legal action.`}{" "}
                </Typography>
              </Stack>

              {/* VICTIM INFORMATION */}
              <Stack gap="16px">
                <Typography variant="subtitle2" fontSize="22px">
                  2. Victim Information
                </Typography>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  2.1. Basic Information
                </Typography>
                <Stack px="16px" className={classes.bgColor}>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Name
                    </Typography>
                    <Typography variant="body1">
                      {clientName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {clientAndCaseDetail?.case?.client?.email
                        ? clientAndCaseDetail?.case?.client?.email
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Contact Number
                    </Typography>
                    <Typography variant="body1">
                      {clientAndCaseDetail?.case?.client?.contact_number
                        ? clientAndCaseDetail?.case?.client?.contact_number
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {clientAndCaseDetail?.case?.client?.address
                        ? clientAndCaseDetail?.case?.client?.address
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Country
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.country
                        ? clientAndCaseDetail?.case?.client?.country
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      State
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.region
                        ? clientAndCaseDetail?.case?.client?.region
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      City
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.city
                        ? clientAndCaseDetail?.case?.client?.city
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Zip Code
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.zipcode
                        ? clientAndCaseDetail?.case?.client?.zipcode
                        : "-"}
                    </Typography>
                  </Box>
                </Stack>
                <Stack px="16px" className={classes.bgColor}>
                  {['dob', 'gender', 'additional_info'].filter(key => report.client?.[key] != null).map(key => <Box display="flex" p="6px 16px" key={key}>
                    <Typography width="185px" flexShrink={0} variant="body1" fontWeight="600">{{dob: 'Date of birth', gender: 'Gender', additional_info: 'Additional information'}[key]}</Typography>
                    <Typography variant="body1">{key === 'dob' ? displayDate(report.client[key]) : displayValue(report.client[key])}</Typography>
                  </Box>)}
                </Stack>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  2.2. Work Details{" "}
                </Typography>
                <Stack px="16px" className={classes.bgColor}>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Job Title
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.job_title
                        ? clientAndCaseDetail?.case?.client?.job_title
                        : "-"}{" "}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Company Name
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.company_name
                        ? clientAndCaseDetail?.case?.client?.company_name
                        : "-"}{" "}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Annual Income
                    </Typography>
                    <Typography variant="body1">
                      {clientAndCaseDetail?.case?.client?.annual_income
                        ? `$${clientAndCaseDetail?.case?.client?.annual_income}`
                        : "-"}{" "}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  2.3. Hobbies & Lifestyle{" "}
                </Typography>
                <Stack px="16px" className={classes.bgColor}>
                  <Box display="flex" alignItems="center" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Hobbies
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.client?.hobbies
                        ? asArray(clientAndCaseDetail?.case?.client?.hobbies)?.join(", ")
                        : "-"}{" "}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {/* CASE DETAILS  */}
              <Stack gap="16px" className={classes.page_break}>
                <Typography variant="subtitle2" fontSize="22px">
                  3. Case Details
                </Typography>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  3.1. Basic Details{" "}
                </Typography>
                <Stack px="16px" className={classes.bgColor}>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Case Open Date
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.case_opening_date
                        ? displayDate(clientAndCaseDetail?.case?.case_opening_date)
                        : "-"}{" "}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Case Filing Date
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.case_registration_date
                        ? displayDate(clientAndCaseDetail?.case?.case_registration_date)
                        : "-"}{" "}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Statute of Limitation{" "}
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.statute_of_limitation
                        ? displayDate(clientAndCaseDetail?.case?.statute_of_limitation)
                        : "-"}{" "}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Date Of Incident
                    </Typography>
                    <Typography variant="body1">
                      {clientAndCaseDetail?.case?.incident_date
                        ? displayDate(clientAndCaseDetail?.case?.incident_date)
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Case Type
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ textTransform: "capitalize" }}
                    >
                      {" "}
                      {clientAndCaseDetail?.case?.case_type
                        ? getCaseValueText(clientAndCaseDetail?.case?.case_type)
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Opposing Party
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.opposing_party
                        ? clientAndCaseDetail?.case?.opposing_party
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Location of Incident
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.incident_location
                        ? clientAndCaseDetail?.case?.incident_location
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography width="185px" variant="body1" fontWeight="600">
                      Jurisdiction
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.jurisdiction
                        ? clientAndCaseDetail?.case?.jurisdiction
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography
                      maxWidth="185px"
                      width="100%"
                      variant="body1"
                      fontWeight="600"
                    >
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.description
                        ? clientAndCaseDetail?.case?.description
                        : "-"}
                    </Typography>
                  </Box>
                  <Box display="flex" p="6px 16px">
                    <Typography
                      maxWidth="185px"
                      width="100%"
                      variant="body1"
                      fontWeight="600"
                    >
                      Discovery Information
                    </Typography>
                    <Typography variant="body1">
                      {" "}
                      {clientAndCaseDetail?.case?.discovery_information
                        ? clientAndCaseDetail?.case?.discovery_information
                        : "-"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack px="16px" className={classes.bgColor}>
                  {['title', 'case_number', 'status', 'case_value', 'witnesses', 'insurance_information', 'court_orders', 'notes'].filter(key => report.case?.[key] != null).map(key => (
                    <Box display="flex" p="6px 16px" key={key}>
                      <Typography width="185px" flexShrink={0} variant="body1" fontWeight="600">{key.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}</Typography>
                      <Typography variant="body1">{displayValue(report.case[key])}</Typography>
                    </Box>
                  ))}
                </Stack>

                {/* INTAKE QUESTIONS */}
                {report.manifest.sections?.intake !== false && intakeRows.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontSize="20px" px="16px">
                      3.2. Intake Information{" "}
                    </Typography>
                    <Stack px="16px" className={classes.bgColor}>
                      <Box display="flex" p="6px 16px" gap="8px">
                        <Typography
                          width="50%"
                          variant="body1"
                          fontWeight="600"
                        >
                          Question
                        </Typography>
                        <Typography variant="body1" width="50%">
                          Answer given by the client
                        </Typography>
                      </Box>

                      {intakeRows.map((answer, index) => (
                        <Box display="flex" p="6px 16px" key={answer.id || index} gap="8px">
                          <Typography width="50%" variant="body1" fontWeight="600">{answer.question_text}</Typography>
                          <Typography variant="body1" width="50%">{displayValue(answer.answer_text)}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </>
                )}
              </Stack>

              {report.manifest.sections?.pain_logs !== false && <>
              {/* Pie Chart */}

              <Stack gap="18px" pb="40px">
                <Typography variant="subtitle2" fontSize="22px">
                  4. Injuries and Sufferings
                </Typography>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  4.1. Overall Pain Statistics{" "}
                </Typography>
                {pieChartStatsState?.worst_pain_entries ||
                pieChartStatsState?.mild_pain_entries ||
                pieChartStatsState?.severe_pain_entries ||
                pieChartStatsState?.no_pain_entries || pieChartStatsState?.moderate_pain_entries ? (
                  <div style={{ width: "250px" }} >
                    <CustomPieChart pieChartStatsState={pieChartStatsState} includeModerate={!!pieChartStatsState.moderate_pain_entries} forReport />
                  </div>
                ) : (
                  <></>
                )}
                <Stack>
                  <Box p="6px 16px" display="flex" alignItems="center">
                    <Box width="12px" height="12px" bgcolor="#F45454"></Box>
                    <Typography
                      variant="body1"
                      mx="8px"
                      fontWeight="600"
                      width="176px"
                    >
                      Days with Worst Pain
                    </Typography>
                    <Typography variant="body1">
                      {pieChartStatsState?.worst_pain_entries || 0} days (
                      {worstPainPercentage.toFixed(2)}% of the recorded period){" "}
                    </Typography>
                  </Box>
                  <Box p="6px 16px" display="flex" alignItems="center">
                    <Box width="12px" height="12px" bgcolor="#AAE77A"></Box>
                    <Typography
                      variant="body1"
                      mx="8px"
                      fontWeight="600"
                      width="176px"
                    >
                      Days with Mild Pain{" "}
                    </Typography>
                    <Typography variant="body1">
                      {pieChartStatsState?.mild_pain_entries || 0} days (
                      {mildPainPercentage.toFixed(2)}% of the recorded period){" "}
                    </Typography>
                  </Box>
                  {!!pieChartStatsState.moderate_pain_entries && <Box p="6px 16px" display="flex" alignItems="center">
                    <Box width="12px" height="12px" bgcolor="#FFAC66" />
                    <Typography variant="body1" mx="8px" fontWeight="600" width="176px">Days with Moderate Pain</Typography>
                    <Typography variant="body1">{pieChartStatsState.moderate_pain_entries} days ({percentage('moderate_pain_entries')}% of the recorded period)</Typography>
                  </Box>}
                  <Box p="6px 16px" display="flex" alignItems="center">
                    <Box width="12px" height="12px" bgcolor="#FFDD66"></Box>
                    <Typography
                      variant="body1"
                      mx="8px"
                      fontWeight="600"
                      width="176px"
                    >
                      Days with Severe{" "}
                    </Typography>
                    <Typography variant="body1">
                      {pieChartStatsState?.severe_pain_entries || 0} days (
                      {severePainPercentage.toFixed(2)}% of the recorded period){" "}
                    </Typography>
                  </Box>
                  <Box p="6px 16px" display="flex" alignItems="center">
                    <Box width="12px" height="12px" bgcolor="#0C80F3 "></Box>
                    <Typography
                      variant="body1"
                      mx="8px"
                      fontWeight="600"
                      width="176px"
                    >
                      Days with No Pain{" "}
                    </Typography>
                    <Typography variant="body1">
                      {pieChartStatsState?.no_pain_entries || 0} days (
                      {noPainPercentage.toFixed(2)}% of the recorded period){" "}
                    </Typography>
                  </Box>
                  {/* <Box p="6px 16px" display="flex" alignItems="center">
                    <Typography
                      variant="body1"
                      mx="8px"
                      fontWeight="600"
                      width="176px"
                    >
                      Average Pain Intensity
                    </Typography>
                    <Typography variant="body1">3.7 out of 5</Typography>
                  </Box> */}
                </Stack>
              </Stack>

              {/* LINE CHARTS  */}
              <Stack gap="18px" className={classes.page_break}>
                <Typography variant="subtitle2" fontSize="20px" px="16px">
                  4.2. Pain Trends{" "}
                </Typography>
                {allBodyParts?.map((bodyPart, index) => (
                  <Box
                    bgcolor="#fcfcfc"
                    padding="0.625rem 0.5rem 0.625rem 1.125rem"
                    borderRadius="8px"
                    key={index}
                    maxWidth="654px"
                    // height="256px"
                  >
                    <CurveChart
                      allDates={chartDates}
                      selectedBodyParts={[bodyPart]}
                      fromPdf={true}
                    />
                  </Box>
                ))}
              </Stack>

              {/* PAIN LOGS */}
              <Stack gap="12px">
                <Typography variant="h6" fontSize="24px" fontWeight="600">
                  Pain Logs
                </Typography>
                <Stack>
                  <Box display="flex" alignItems="center" gap="16px">
                    <Typography
                      variant="body1"
                      fontSize="16px"
                      fontWeight="600"
                      width="150px"
                    >
                      Name:
                    </Typography>
                    <Typography variant="body1" fontSize="16px">
                      {clientName}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap="16px">
                    <Typography
                      variant="body1"
                      fontSize="16px"
                      fontWeight="600"
                      width="150px"
                    >
                      Contact:
                    </Typography>
                    {(clientAndCaseDetail?.case?.client?.email ||
                      clientAndCaseDetail?.case?.client?.contact_number) && (
                      <Typography variant="body1" fontSize="16px">
                        {clientAndCaseDetail?.case?.client?.email}
                        {clientAndCaseDetail?.case?.client?.contact_number &&
                          ` | ${clientAndCaseDetail?.case?.client?.contact_number}`}
                      </Typography>
                    )}
                  </Box>
                  {clientAndCaseDetail?.case?.incident_date && (
                    <Box display="flex" alignItems="center" gap="16px">
                      <Typography
                        variant="body1"
                        fontSize="16px"
                        fontWeight="600"
                        width="150px"
                      >
                        Date of Incident:{" "}
                      </Typography>
                      <Typography variant="body1" fontSize="16px">
                        {onlyDateFormat(
                          clientAndCaseDetail?.case?.incident_date
                        )}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Stack>

              {/* PAIN LOG TABLE */}
              {painRows.length > 0 && (
                <TableContainer className={classes.page_break}>
                  <Table aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell width="70px">Sr No</TableCell>
                        <TableCell width="110px" align="left">
                          Date
                        </TableCell>
                        <TableCell align="left" width="100px">
                          Time
                        </TableCell>
                        <TableCell align="left" width="130px">
                          Body Part
                        </TableCell>
                        <TableCell align="left" width="130px">
                          Pain Intensity
                        </TableCell>
                        <TableCell align="left" width="130px">
                          Comments
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody className={classes.bgColor}>
                      {painRows.map((row, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {index + 1}
                          </TableCell>
                          <TableCell align="left">
                            {displayDate(row.date)}
                          </TableCell>
                          <TableCell align="left">{row.time}</TableCell>
                          <TableCell
                            align="left"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {row.bodyPart}
                          </TableCell>
                          <TableCell align="left">{displayValue(row.intensity)}</TableCell>
                          <TableCell align="left">{row.comments}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              </>}
              {report.manifest.sections?.expenses !== false && <ReportRecords title="5. Financial Impact" rows={expenses} columns={[
                ['date', 'Date', row => displayDate(row.date)], ['title', 'Expense'], ['expense_type', 'Type'], ['total_amount', 'Amount'], ['notes', 'Notes']
              ]} classes={classes} details={[
                ['location', 'Location'], ['hours_missed', 'Hours missed'], ['salary_type', 'Salary type'], ['hourly_wage', 'Hourly wage'],
                ['monthly_salary', 'Monthly salary'], ['tips', 'Tips'], ['mileage', 'Mileage'], ['transport_type', 'Transport type'],
                ['fuel_price', 'Fuel price'], ['transport_from', 'From'], ['transport_to', 'To']
              ]} />}
              {report.manifest.sections?.expenses !== false && expenses.flatMap((expense, expenseIndex) => asArray(expense.expense_attachments).map((document, index) =>
                <ReportAttachment key={`expense-${expenseIndex}-${index}`} attachmentKey={`expense-${expenseIndex}-${index}`} document={document} onStatus={handleAttachmentStatus} />))}
              {report.manifest.sections?.appointments !== false && <ReportRecords title="7. Detailed Event Logs" rows={appointments} columns={[
                ['source_table', 'Record type', row => row.source_table === 'appointments' ? 'Appointment series' : 'Appointment occurrence'],
                ['date', 'Date', row => displayDate(row.date || row.start_date)], ['title', 'Appointment'], ['start_time', 'Start'],
                ['end_time', 'End'], ['appointment_status', 'Status'], ['notes', 'Notes']
              ]} classes={classes} details={[
                ['reason_of_missing', 'Reason missed'], ['reason_of_rescheduling', 'Reason rescheduled'],
                ['end_date', 'End date'], ['recurring_frequency', 'Recurring frequency']
              ]} />}
              {report.manifest.sections?.documents !== false && documents.map((document, index) => (
                <Stack className={classes.page_break} key={`document-${index}`}>
                  <ReportAttachment attachmentKey={`document-${index}`} document={document} onStatus={handleAttachmentStatus} />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </div>

  );
}
